<template>
  <v-tabs
    v-model="selectedTab"
    align-with-title
    class="edit-subscription-tabs"
    color="orange-base"
    bg-color="white"
    slider-color="orange-base"
    :show-arrows="false"
    :height="48"
  >
    <v-tab class="mr-4" value="details">{{ $t("general.details") }}</v-tab>
    <v-tab
      v-if="showUserDeviceLimitsTab"
      class="mr-4"
      value="user-device-limits"
      data-testid="edit-workspace-modal-limit-protected-users"
      >{{ $t("modals.editWorkspace.userDeviceLimits.tabTitle") }}</v-tab
    >
    <v-tab v-if="showSubscriptionRulesTab" value="subscription-rules">{{
      $t("modals.createWorkspace.subscriptionRules")
    }}</v-tab>
  </v-tabs>
  <v-tabs-window v-model="selectedTab">
    <v-tabs-window-item eager value="details">
      <v-form ref="detailsForm">
        <v-row>
          <v-col :cols="6">
            <v-text-field
              v-model="localValue.item.companyName"
              type="text"
              data-testid="edit-workspace-modal-company-name"
              class="mt-6"
              :rules="companyNameRules"
              variant="outlined"
              :label="$t('modals.editWorkspace.companyName')"
              :aria-label="$t('modals.editWorkspace.companyName')"
            ></v-text-field>
            <v-text-field
              v-model="localValue.item.displayName"
              type="text"
              data-testid="edit-workspace-modal-display-name"
              class="mt-3"
              counter="30"
              :rules="displayNameRules"
              :label="$t('modals.editWorkspace.displayName')"
              :aria-label="$t('modals.editWorkspace.displayName')"
              :placeholder="$t('modals.editWorkspace.displayName')"
              :hint="$t('modals.editWorkspace.displayNameHint')"
              persistent-hint
              variant="outlined"
            ></v-text-field>
            <cui-locale-select
              v-model="localValue.item.languageCode"
              :locale-labels="localeDisplayNames"
              :labels="localeSelectLabels"
              test-id-prefix="edit-workspace-modal"
            />

            <template v-if="isChildWorkspace">
              <div class="subtitle1 mt-4 mb-4">
                {{ $t("modals.createWorkspace.globalProtectionPoliciesTitle") }}
              </div>
              <v-select
                v-model="localValue.item.mspPolicyId"
                :items="globalPolicies.items"
                data-testid="global-policy-select"
                class="mb-4"
                item-value="id"
                item-title="name"
                :label="$t('modals.createWorkspace.mspPolicyIdLabel')"
                :placeholder="$t('modals.createWorkspace.mspPolicyIdLabel')"
                variant="outlined"
                hide-details="auto"
                auto-select-first="exact"
                :loading="globalPoliciesLoading"
              >
                <template #append-item>
                  <div v-intersect="onWorkspacePolicyIntersect" />
                </template>
              </v-select>
              <v-switch
                v-model="localValue.item.allowPolicyOverriding"
                :ripple="false"
                class="mb-2"
                density="compact"
                hide-details
                color="primary"
                inset
                flat
                data-testid="allow-local-overriding-switch"
              >
                <template #label>
                  <div class="body2">
                    {{ $t("modals.createWorkspace.allowLocalOverriding") }}
                  </div>
                </template>
              </v-switch>
            </template>

            <cui-billing-fields
              v-if="isGlobalContext"
              v-model="localValue.item"
              :workspace-type="localValue.item.type"
              :parent-workspace-id="localValue.item.parentWorkspaceId"
              :labels="billingLabels"
              test-id-prefix="edit-workspace"
            />

            <cui-notification-level-select
              v-model="localValue.item.notificationsLevel"
              :workspace-type="localValue.item.type"
              :has-parent="hasParentWorkspace"
              :labels="notificationLabels"
              test-id-prefix="edit-workspace-modal"
            />
            <cui-workspace-advanced-settings
              v-model="localValue.item"
              :switches="advancedSettingSwitches"
              :labels="advancedSettingsLabels"
              test-id-prefix="edit-workspace-modal"
            />

            <template v-if="isChannelWorkspace">
              <div class="subtitle1 mt-4 mb-4">
                {{ $t("modals.editWorkspace.branding.title") }}
              </div>
              <div class="mt-3 mb-4 px-3 py-1 info-block">
                <v-icon size="24" icon="$info" />
                <span class="body2 ml-2">
                  {{ $t("modals.editWorkspace.branding.infoBlock") }}
                </span>
              </div>
              <cui-branding-fields
                v-model="localValue.item.branding"
                :labels="brandingLabels"
                :rules="brandingRules"
                :no-reply-domain="brandingNoReplyDomain"
                :alias-disabled="!isGlobalAdmin"
                test-id-prefix="edit-workspace-modal"
              />
            </template>
          </v-col>
          <v-col :cols="6">
            <cui-branding-logos
              v-if="isChannelWorkspace"
              class="mt-6 ml-12 pl-6"
              :header-icon-url="localValue.item.branding.headerIconUrl"
              :email-icon-url="localValue.item.branding.emailIconUrl"
              :brand-color="localValue.item.branding.brandColor"
              :default-logos="defaultLogos"
              :labels="logosLabels"
              test-id-prefix="edit-workspace-modal"
              @image-changed="localValue.item.image = $event"
              @email-image-changed="localValue.item.emailImage = $event"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-tabs-window-item>
    <v-tabs-window-item v-if="showSubscriptionRulesTab" eager value="subscription-rules">
      <v-form ref="subscriptionForm">
        <cui-subscription-rules-card
          :hidden-modules="hiddenModules"
          prune-hidden-modules
          v-model:subscription-config="localValue.item.subscriptionRules"
          :bundle-or-module-is-required-message="$t('validations.bundleOrModuleIsRequired')"
          :validation-aria-label="$t('ariaLabels.subscriptionRulesValidation')"
          :allowed-bundles-label="$t('subscriptions.allowedBundles')"
          :choose-available-bundles-label="$t('subscriptions.chooseAvailableBundles')"
          :new-bundles-label="$t('subscriptions.newBundles')"
          :legacy-bundles-label="$t('subscriptions.legacyBundles')"
          :allowed-modules-label="$t('subscriptions.allowedModules')"
          :choose-allowed-modules-label="$t('subscriptions.chooseAllowedModules')"
          v-bind="subscriptionRulesNames"
          :please-select-at-least-one-option-message="$t('validations.bundleOrModuleIsRequired')"
          :parent-workspace-subscription-config="parentWorkspaceSubscriptionRules"
        />
      </v-form>
    </v-tabs-window-item>
    <v-tabs-window-item v-if="showUserDeviceLimitsTab" eager value="user-device-limits">
      <v-form ref="limitsForm">
        <div class="mt-6">
          <cui-workspace-limits-card
            v-model:limited="localValue.item.limited"
            v-model:limits="localValue.item.limits"
            :original-protected-users="config.item.protectedUsers"
            :labels="limitsLabels"
          />
        </div>
      </v-form>
    </v-tabs-window-item>
  </v-tabs-window>
</template>

<script lang="ts">
import { WorkspaceType } from "@/constants/workplaces";
import cloneDeep from "lodash/cloneDeep";
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import type { VForm } from "vuetify/components";
import { useMspWorkspaceModal } from "@/composables/useMspWorkspaceModal";
import { useWorkspaceFormText } from "@/composables/useWorkspaceFormText";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import {
  type MspWorkspaceDialogItem,
  type SubscriptionRulesConfig,
  useMspStore,
} from "@/_store/msp/msp.module";
import { storeToRefs } from "pinia";
import { usePermissionsStore } from "@/_store/permissions.module.ts";
import { handleVirtualScrollData } from "@coronet/cui";
import type { Paged } from "@/types.ts";
import {
  type GlobalPolicyListItem,
  useMspGlobalPoliciesStore,
} from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import {
  CuiBillingFields,
  CuiBrandingFields,
  type CuiBrandingFieldsRules,
  CuiBrandingLogos,
  CuiLocaleSelect,
  CuiNotificationLevelSelect,
  CuiSubscriptionRulesCard,
  CuiWorkspaceAdvancedSettings,
  CuiWorkspaceLimitsCard,
  getHiddenSubscriptionModules,
  useCuiTabbedForms,
  WorkspaceAdvancedSetting,
} from "@coronet/cui";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import {
  BRANDING_DEFAULT_LOGOS,
  BRANDING_NO_REPLY_DOMAIN,
  isCanadaEnv,
} from "@/constants/general.ts";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<MspWorkspaceDialogItem>>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    CuiSubscriptionRulesCard,
    CuiBrandingFields,
    CuiBrandingLogos,
    CuiWorkspaceLimitsCard,
    CuiBillingFields,
    CuiLocaleSelect,
    CuiNotificationLevelSelect,
    CuiWorkspaceAdvancedSettings,
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const selectedTab = ref<"details" | "subscription-rules" | "user-device-limits">("details");
    const permissionsStore = usePermissionsStore();
    const workspaceStore = useWorkspaceStore();
    const mspGlobalPoliciesStore = useMspGlobalPoliciesStore();
    const { isGlobalAdmin } = storeToRefs(permissionsStore);
    const localValue = ref(cloneDeep(props.config));
    const detailsForm = ref<InstanceType<typeof VForm>>();
    const subscriptionForm = ref<InstanceType<typeof VForm>>();
    const limitsForm = ref<InstanceType<typeof VForm>>();

    // single source of truth: tab value ↔ its form ref
    const forms = [
      { tab: "details", form: detailsForm },
      { tab: "subscription-rules", form: subscriptionForm },
      { tab: "user-device-limits", form: limitsForm },
    ] as const;

    // Save-button state (update:valid) and validate() for the dialog host.
    const { valid, validate } = useCuiTabbedForms(forms, selectedTab);
    const globalPolicies = ref<Paged<GlobalPolicyListItem>>({
      items: [],
      total: 0,
    });
    const globalPoliciesLoading = ref(false);
    const globalPoliciesSearchPage = ref(0);
    const hasMorePolicies = ref(true);
    const parentWorkspaceSubscriptionRules = ref<SubscriptionRulesConfig | undefined>(undefined);
    const { domainRules, companyNameRules, displayNameRules, brandColorRules, supportEmailRules } =
      useMspWorkspaceModal();
    const {
      billingLabels,
      limitsLabels,
      brandingLabels,
      logosLabels,
      localeSelectLabels,
      localeDisplayNames,
      subscriptionRulesNames,
      advancedSettingsLabels,
      notificationLevelLabels,
    } = useWorkspaceFormText();
    // No alias rule in edit: the alias is validated only when a workspace is created.
    const brandingRules: CuiBrandingFieldsRules = { brandColorRules, supportEmailRules };

    const isChannelWorkspace = computed(() => props.config.item.type === WorkspaceType.CHANNEL);
    const isChildWorkspace = computed(() => props.config.item.type === WorkspaceType.CHILD);
    const isRegularWorkspace = computed(() => props.config.item.type === WorkspaceType.REGULAR);
    const hiddenModules = computed(() =>
      getHiddenSubscriptionModules([props.config.item.type], { hideSat: isCanadaEnv })
    );
    const showUserDeviceLimitsTab = computed(
      () => isChildWorkspace.value || isRegularWorkspace.value
    );
    const isGlobalContext = computed(
      () => localValue.value.item.context === ManageWorkspacesContext.GLOBAL_WORKSPACES
    );

    const showSubscriptionRulesTab = computed<boolean>(() => {
      const isSubchannelHierarchyWorkspace =
        isChannelWorkspace.value &&
        localValue.value.item.workspaceId !== workspaceStore.workspace.workspaceId;

      if (isGlobalContext.value) {
        return isChannelWorkspace.value || isRegularWorkspace.value;
      }

      // edge case for when MSP is accessed as a global admin user,
      // then ROOT channel workspace subscription rules can still be edited
      if (isGlobalAdmin.value) {
        return isChannelWorkspace.value;
      }

      return isSubchannelHierarchyWorkspace;
    });

    const domainDisplayName = computed(() => {
      return localValue.value.item.type === WorkspaceType.REGULAR
        ? "coro.net"
        : localValue.value.item.parentDomain;
    });

    const showScanUnprotectedUsers = computed(() => {
      if (localValue.value.item.type === WorkspaceType.CHILD) {
        return localValue.value.item.parentScanUnprotectedUsers;
      }
      return isChannelWorkspace.value;
    });

    const hasParentWorkspace = computed(
      () => !!(localValue.value.item.parentWorkspaceId || localValue.value.item.parentDomain)
    );

    const notificationLabels = computed(() =>
      notificationLevelLabels({
        workspaceType: localValue.value.item.type,
        hasParent: hasParentWorkspace.value,
        domain: domainDisplayName.value,
      })
    );

    const advancedSettingSwitches = computed(() => [
      ...(isChannelWorkspace.value ? [WorkspaceAdvancedSetting.SUPPORT_ENABLED] : []),
      ...(showScanUnprotectedUsers.value ? [WorkspaceAdvancedSetting.SCAN_UNPROTECTED_USERS] : []),
      WorkspaceAdvancedSetting.SHOW_DISABLED_MODULES,
      ...(isGlobalAdmin.value ? [WorkspaceAdvancedSetting.CORO_EMPLOYEE_WORKSPACE] : []),
    ]);

    const fetchGlobalPolicies = async () => {
      if (props.disable) return;
      globalPoliciesLoading.value = true;

      const { data } = await mspGlobalPoliciesStore.searchGlobalPolicies("", {
        page: globalPoliciesSearchPage.value,
        pageSize: 25,
      });
      hasMorePolicies.value = globalPolicies.value.items.length < data.total;
      globalPolicies.value = {
        items: handleVirtualScrollData(globalPolicies.value.items, data.items, "id"),
        total: data.total,
      };
      globalPoliciesLoading.value = false;
    };

    const { onIntersect: onWorkspacePolicyIntersect } = useInfiniteScroll(
      computed(() => globalPolicies.value.items),
      { hasMore: hasMorePolicies },
      () => {
        globalPoliciesSearchPage.value += 1;
        return fetchGlobalPolicies();
      }
    );

    watch(
      localValue,
      (newVal) => {
        emit("update:localValue", newVal);
      },
      { deep: true }
    );

    watch(valid, (value: boolean | null) => {
      emit("update:valid", value ?? true);
    });

    const initGlobalPoliciesIfNeeded = async () => {
      if (isChildWorkspace.value) {
        await fetchGlobalPolicies();
      }
    };

    const initSubscriptionRulesState = async () => {
      if (!showSubscriptionRulesTab.value) {
        // If the subscription rules tab is not shown, clear the subscription rules as they don't need to be sent to BE
        localValue.value.item.subscriptionRules = undefined;
        return;
      }

      if (isChannelWorkspace.value && !!localValue.value.item.parentWorkspaceId) {
        // For subchannel workspaces we need to fetch parent workspace subscription rules to limit their editing
        parentWorkspaceSubscriptionRules.value = await useMspStore().getWorkspaceSubscriptionRules(
          localValue.value.item.parentWorkspaceId
        );
      }
    };

    onMounted(async () => {
      await initGlobalPoliciesIfNeeded();
      await initSubscriptionRulesState();

      emit("update:localValue", localValue.value);
    });

    return {
      valid,
      validate,
      detailsForm,
      subscriptionForm,
      limitsForm,
      domainRules,
      companyNameRules,
      displayNameRules,
      isChannelWorkspace,
      isChildWorkspace,
      isRegularWorkspace,
      hiddenModules,
      parentWorkspaceSubscriptionRules,
      domainDisplayName,
      showScanUnprotectedUsers,
      localValue,
      isGlobalAdmin,
      hasParentWorkspace,
      notificationLabels,
      advancedSettingSwitches,
      advancedSettingsLabels,
      globalPolicies,
      onWorkspacePolicyIntersect,
      globalPoliciesLoading,
      selectedTab,
      ManageWorkspacesContext,
      hasMorePolicies,
      showSubscriptionRulesTab,
      showUserDeviceLimitsTab,
      subscriptionRulesNames,
      isGlobalContext,
      billingLabels,
      limitsLabels,
      brandingLabels,
      brandingRules,
      brandingNoReplyDomain: BRANDING_NO_REPLY_DOMAIN,
      logosLabels,
      defaultLogos: BRANDING_DEFAULT_LOGOS,
      localeSelectLabels,
      localeDisplayNames,
    };
  },
});
</script>

<style lang="scss" scoped>
.edit-subscription-tabs {
  position: sticky;
  top: -16px;
  z-index: 100;
}
</style>
